import { T } from "@freightsimple/ui-core/Typography/T";
import type { IconWeight } from "@phosphor-icons/react/dist/lib/types";
import { ClipboardIcon } from "@phosphor-icons/react/Clipboard";
import { ClockIcon } from "@phosphor-icons/react/Clock";
import { TruckIcon } from "@phosphor-icons/react/Truck";
import type { ComponentType, CSSProperties, ReactNode } from "react";
import ShipmentSection from "@/Shared/Shipment/CustomComponents/ShipmentSection";

interface NextStepsForDeliverySectionProps {
  deliveringByAppointment: boolean;
  appointmentSet: boolean;
}

interface SimpleStep {
  icon: ComponentType<{
    className?: string;
    weight?: IconWeight;
    style?: CSSProperties;
  }>;
  // A string (not ReactNode) so it can double as a stable React key
  stepName: string;
  stepDescription: ReactNode;
}

interface SimpleStepsProps {
  steps: SimpleStep[];
}

function SimpleSteps_(props: SimpleStepsProps) {
  return (
    <div
      className="flex flex-row items-start justify-between gap-[16px]"
      style={{ marginLeft: "-16px" }}
    >
      {props.steps.map(function (step) {
        const StepIcon = step.icon;
        return (
          <div className="flex flex-col items-start" key={step.stepName}>
            <StepIcon
              className="h-12 w-12"
              weight="fill"
              style={{ color: "var(--color-light-gray)" }}
            />
            <div className="h-[8px]" />
            <T size="xl" weight="semibold">
              {step.stepName}
            </T>
            <div className="max-w-[300px] text-sm font-normal text-foreground">
              {step.stepDescription}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ShipmentNextStepsForDeliverySection(
  props: NextStepsForDeliverySectionProps,
) {
  const steps: SimpleStep[] = [];

  steps.push({
    stepName: "Track your shipment here",
    icon: TruckIcon,
    stepDescription: (
      <>
        No need to track on the carrier's site. FreightSimple always has the
        best information about a shipment
      </>
    ),
  });

  if (props.deliveringByAppointment) {
    steps.push({
      stepName: "Delivery Appointment",
      icon: ClockIcon,
      stepDescription: props.appointmentSet ? (
        <>
          The carrier has informed us that an appointment has been set with the
          delivery contact for this
        </>
      ) : (
        <>
          When the freight is close to delivery, the carrier will call to make
          an appointment for delivery at a convenient time
        </>
      ),
    });
  } else {
    steps.push({
      stepName: "No Delivery Appointment",
      icon: ClockIcon,
      stepDescription: (
        <>This will deliver as soon as possible within the provided hours</>
      ),
    });
  }

  steps.push({
    stepName: "At Delivery",
    icon: ClipboardIcon,
    stepDescription: (
      <>
        When receiving freight, make sure any damage is noted when you sign the
        proof-of-delivery
      </>
    ),
  });

  return (
    <ShipmentSection title={undefined} id="next-steps-section">
      <SimpleSteps_ steps={steps} />
    </ShipmentSection>
  );
}
export default ShipmentNextStepsForDeliverySection;
